/**
 * Bidirectional slug <-> Algolia facet value mappings for /propiedades SEO URLs.
 *
 * URL pattern: /propiedades/{operacion?}/{loc0?}/{loc1?}/{loc2?}/{loc3?}/{tipo?}
 * All filters are independent — any combination is valid.
 * Segments are disambiguated by checking known slug sets (operation, tipo, location).
 */

// ---------------------------------------------------------------------------
// Generic slug utilities
// ---------------------------------------------------------------------------

/** Deterministic, SEO-friendly slug: lowercase ASCII with hyphens. */
export function slugify(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // strip diacritics
    .toLowerCase()
    .replace(/['`']/g, '-')          // apostrophes → hyphen
    .replace(/[^a-z0-9]+/g, '-')     // non-alphanumeric → hyphen
    .replace(/^-|-$/g, '');           // trim leading/trailing
}

/**
 * Best-effort inverse of slugify: hyphens → spaces, title-case each word.
 * Lossy (cannot restore accents or apostrophes), but good enough for
 * matching against HierarchicalMenu facets on the client.
 */
export function deslugify(slug: string): string {
  return slug
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// ---------------------------------------------------------------------------
// Operation
// ---------------------------------------------------------------------------
// El valor de traspaso llega desde el facet derivado `operation_facet_es` de Algolia
// (`transfer ? 'Traspaso' : operation_type_es`), no desde `operation_type_es`: en Witei
// los traspasos se codifican como alquiler porque es donde cabe la mensualidad del
// local. Ver traspasos-plan.md §0.
const OPERATION_SLUG_TO_VALUE: Record<string, string> = {
  venta: 'Venta',
  alquiler: 'Alquiler',
  traspaso: 'Traspaso',
};

/** Un traspaso es siempre una venta de negocio, nunca un alquiler. */
export const TRASPASO_OPERACION_VALUE = 'Traspaso';

const OPERATION_VALUE_TO_SLUG: Record<string, string> = Object.fromEntries(
  Object.entries(OPERATION_SLUG_TO_VALUE).map(([slug, value]) => [value, slug]),
);

// ---------------------------------------------------------------------------
// Property type
// ---------------------------------------------------------------------------
const TYPE_SLUG_TO_VALUE: Record<string, string> = {
  pisos: 'Piso',
  chalets: 'Chalet',
  locales: 'Local',
  terrenos: 'Terreno',
  aticos: 'Ático',
  'naves-industriales': 'Nave industrial',
  oficinas: 'Oficina',
  'plazas-de-parking': 'Plaza de parking',
  edificios: 'Edificio',
  habitaciones: 'Habitación',
  trasteros: 'Trastero',
};

const TYPE_SLUG_ALIASES: Record<string, string> = {
  chalet: 'Chalet',
};

const TYPE_VALUE_TO_SLUG: Record<string, string> = Object.fromEntries(
  Object.entries(TYPE_SLUG_TO_VALUE).map(([slug, value]) => [value, slug]),
);

// ---------------------------------------------------------------------------
// Location lvl0 (hardcoded for exact accent/case matching)
// ---------------------------------------------------------------------------
const LOCATION_LVL0_SLUG_TO_VALUE: Record<string, string> = {
  valencia: 'Valencia',
  alicante: 'Alicante',
  castellon: 'Castellón',
  'ciudad-real': 'Ciudad Real',
  'islas-baleares': 'Islas Baleares',
  pontevedra: 'Pontevedra',
};

const LOCATION_LVL0_VALUE_TO_SLUG: Record<string, string> = Object.fromEntries(
  Object.entries(LOCATION_LVL0_SLUG_TO_VALUE).map(([slug, value]) => [value, slug]),
);

// ---------------------------------------------------------------------------
// Validation helpers
// ---------------------------------------------------------------------------
export function isValidOperacion(slug: string): boolean {
  return slug in OPERATION_SLUG_TO_VALUE;
}

export function isValidTipo(slug: string): boolean {
  return slug in TYPE_SLUG_TO_VALUE || slug in TYPE_SLUG_ALIASES;
}

// ---------------------------------------------------------------------------
// Slug <-> Algolia value conversion
// ---------------------------------------------------------------------------
export function operacionSlugToValue(slug: string): string | undefined {
  return OPERATION_SLUG_TO_VALUE[slug];
}

export function operacionValueToSlug(value: string): string | undefined {
  return OPERATION_VALUE_TO_SLUG[value];
}

export function tipoSlugToValue(slug: string): string | undefined {
  return TYPE_SLUG_TO_VALUE[slug] ?? TYPE_SLUG_ALIASES[slug];
}

export function tipoValueToSlug(value: string): string | undefined {
  return TYPE_VALUE_TO_SLUG[value];
}

/**
 * Convert a location value to a slug.
 * Uses the hardcoded lvl0 map when available, falls back to generic slugify.
 */
export function locationValueToSlug(value: string): string {
  return LOCATION_LVL0_VALUE_TO_SLUG[value] ?? slugify(value);
}

/**
 * Convert a location slug back to an Algolia value (best effort).
 * Uses the hardcoded lvl0 map when available, falls back to generic deslugify.
 */
export function locationSlugToValue(slug: string): string {
  return LOCATION_LVL0_SLUG_TO_VALUE[slug] ?? deslugify(slug);
}

// Keep backwards compat exports used by other files
export const ubicacionValueToSlug = locationValueToSlug;
export const ubicacionSlugToValue = locationSlugToValue;

// ---------------------------------------------------------------------------
// Parse URL segments into filter values
// ---------------------------------------------------------------------------

export interface PropiedadesFilters {
  operacion?: string;   // Algolia value, e.g. "Venta"
  ubicacion?: string[]; // Array of location level values, e.g. ["Valencia", "Valencia Ciudad", "Extramurs"]
  tipo?: string;        // Algolia value, e.g. "Chalet"
}

/**
 * Parse the catch-all `filters` segments from the URL into Algolia values.
 *
 * Operation (venta/alquiler) is mandatory: the middleware redirects any
 * `/propiedades/...` path that doesn't start with a known operation slug,
 * so by the time this function runs the first segment is guaranteed to be
 * a valid operation. As a safety net, any URL reaching this parser without
 * an operation returns `null` (→ 404), which also covers other clearly
 * invalid URLs (e.g. too many location levels).
 *
 * The three slug sets (operation, tipo, location) are non-overlapping, so
 * remaining segments can be unambiguously classified:
 *   1. From the rest, if the last is a known tipo slug → consume as tipo
 *   2. Everything left is the location hierarchy (max 4 levels)
 */
export function parseFilterSegments(
  segments: string[] | undefined,
): PropiedadesFilters | null {
  if (!segments || segments.length === 0) return null;

  const rest = [...segments];

  const operacion = operacionSlugToValue(rest[0]);
  if (!operacion) return null;
  rest.shift();

  const result: PropiedadesFilters = { operacion };

  if (rest.length === 0) return result;

  const lastSeg = rest[rest.length - 1];
  const tipo = tipoSlugToValue(lastSeg);
  if (tipo) {
    result.tipo = tipo;
    rest.pop();
  }

  if (rest.length > 0) {
    if (rest.length > 4) return null; // max 4 location levels (lvl0-lvl3)
    result.ubicacion = rest.map(locationSlugToValue);
  }

  return result;
}

/**
 * Los segmentos de ubicación de una URL de /propiedades, en slug: sin la
 * operación ni el tipo final. Misma regla que `parseFilterSegments`.
 */
export function locationSegments(segments: string[]): string[] {
  const rest = segments.slice(1);
  if (rest.length && isValidTipo(rest[rest.length - 1])) rest.pop();
  return rest;
}

// ---------------------------------------------------------------------------
// Build a URL path from filter values
// ---------------------------------------------------------------------------

export const DEFAULT_OPERACION_VALUE = 'Venta';

/**
 * Build a /propiedades path. The operation is mandatory — if not provided,
 * the path defaults to `venta` so we never emit an operation-less URL into
 * the link graph or the address bar.
 */
export function buildPropiedadesPath(filters: PropiedadesFilters): string {
  const parts = ['/propiedades'];

  const operacionValue = filters.operacion ?? DEFAULT_OPERACION_VALUE;
  const opSlug = operacionValueToSlug(operacionValue) ?? 'venta';
  parts.push(opSlug);

  if (filters.ubicacion?.length) {
    for (const loc of filters.ubicacion) parts.push(locationValueToSlug(loc));
  }

  const typeSlug = filters.tipo ? tipoValueToSlug(filters.tipo) : undefined;
  if (typeSlug) parts.push(typeSlug);

  return parts.join('/');
}

/**
 * Build the HierarchicalMenu value (deepest path) from the ubicacion array.
 * e.g. ["Valencia", "Valencia Ciudad", "Extramurs"] → "Valencia > Valencia Ciudad > Extramurs"
 */
export function buildHierarchicalPath(ubicacion: string[]): string {
  return ubicacion.join(' > ');
}

/**
 * Split a HierarchicalMenu path into individual level values.
 * e.g. "Valencia > Valencia Ciudad > Extramurs" → ["Valencia", "Valencia Ciudad", "Extramurs"]
 */
export function splitHierarchicalPath(path: string): string[] {
  return path.split(' > ');
}

// ---------------------------------------------------------------------------
// SEO metadata helpers
// ---------------------------------------------------------------------------

export const TIPO_PLURAL_DISPLAY: Record<string, string> = {
  Piso: 'Pisos',
  Chalet: 'Chalets',
  Local: 'Locales',
  Terreno: 'Terrenos',
  Ático: 'Áticos',
  'Nave industrial': 'Naves industriales',
  Oficina: 'Oficinas',
  'Plaza de parking': 'Plazas de parking',
  Edificio: 'Edificios',
  Habitación: 'Habitaciones',
  Trastero: 'Trasteros',
};

export function generateSeoTitle(filters: PropiedadesFilters): string {
  const { operacion, ubicacion, tipo } = filters;
  const op = operacion?.toLowerCase();
  const loc = ubicacion?.length ? ubicacion[ubicacion.length - 1] : undefined;
  const tipoPlural = tipo ? TIPO_PLURAL_DISPLAY[tipo] ?? tipo : undefined;
  // Lo que se traspasa es un negocio, no una propiedad. Con tipo la frase ya sale sola
  // ("Locales en traspaso en Valencia"); sin tipo hace falta cambiar el sustantivo.
  const noun = operacion === TRASPASO_OPERACION_VALUE ? 'Negocios' : 'Propiedades';

  if (tipoPlural && loc && op) return `${tipoPlural} en ${op} en ${loc}`;
  if (tipoPlural && loc)        return `${tipoPlural} en ${loc}`;
  if (tipoPlural && op)         return `${tipoPlural} en ${op}`;
  if (loc && op)                return `${noun} en ${op} en ${loc}`;
  if (tipoPlural)               return `${tipoPlural} en venta y alquiler`;
  if (loc)                      return `${noun} en ${loc}`;
  if (op)                       return `${noun} en ${op}`;
  return 'Propiedades en venta y alquiler';
}

export function generateSeoDescription(filters: PropiedadesFilters): string {
  const { operacion, ubicacion, tipo } = filters;
  const op = operacion?.toLowerCase();
  const loc = ubicacion?.length ? ubicacion[ubicacion.length - 1] : undefined;
  const isTraspaso = operacion === TRASPASO_OPERACION_VALUE;
  const tipoPlural = tipo
    ? (TIPO_PLURAL_DISPLAY[tipo] ?? tipo).toLowerCase()
    : isTraspaso ? 'negocios' : 'propiedades';

  // El traspaso se compra: el importe único es el precio y el alquiler del local es un
  // coste que asume quien entra. La descripción lo dice para que no se confundan.
  if (isTraspaso) {
    const where = loc ? ` en ${loc}` : ' en Valencia';
    return `Encuentra ${tipoPlural} en traspaso${where}. Precio del traspaso y alquiler mensual del local en cada anuncio, con Person Inmobiliaria.`;
  }

  if (loc && op) {
    return `Encuentra ${tipoPlural} en ${op} en ${loc}. Descubre nuestra selección de inmuebles con Person Inmobiliaria.`;
  }
  if (loc) {
    return `Encuentra ${tipoPlural} en ${loc}. Descubre nuestra selección de inmuebles con Person Inmobiliaria.`;
  }
  if (op) {
    return `Encuentra ${tipoPlural} en ${op}. Explora nuestra selección de inmuebles en Valencia con Person Inmobiliaria.`;
  }
  return 'Encuentra la propiedad que estabas buscando para alquilar o comprar por tipo, barrio o más características únicas en nuestra alta selección de propiedades.';
}

// ---------------------------------------------------------------------------
// All valid slugs (for sitemap generation)
// ---------------------------------------------------------------------------
export function getAllOperacionSlugs(): string[] {
  return Object.keys(OPERATION_SLUG_TO_VALUE);
}

export function getAllUbicacionSlugs(): string[] {
  return Object.keys(LOCATION_LVL0_SLUG_TO_VALUE);
}

export function getAllTipoSlugs(): string[] {
  return Object.keys(TYPE_SLUG_TO_VALUE);
}

// ---------------------------------------------------------------------------
// All Algolia facet values (for SEO link generation)
// ---------------------------------------------------------------------------
export function getAllOperacionValues(): string[] {
  return Object.values(OPERATION_SLUG_TO_VALUE);
}

export function getAllLocationLvl0Values(): string[] {
  return Object.values(LOCATION_LVL0_SLUG_TO_VALUE);
}

export function getAllTipoValues(): string[] {
  return Object.values(TYPE_SLUG_TO_VALUE);
}

